import { useState } from 'react';
import { Camera, Keyboard, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useLeitorCodigo } from '@/hooks/useLeitorCodigo';
import { MENSAGEM_ERRO_CAMERA } from '@/lib/scanner/camera';

interface ScannerCodigoProps {
  isOpen: boolean;
  onClose: () => void;
  /** Chamado a cada código lido. O modal segue aberto para a próxima peça. */
  onLeitura: (codigo: string) => void;
  /** Retorno da última leitura, exibido sobre a câmera. */
  aviso?: { tipo: 'sucesso' | 'erro'; texto: string } | null;
}

export function ScannerCodigo({ isOpen, onClose, onLeitura, aviso }: ScannerCodigoProps) {
  const [codigoManual, setCodigoManual] = useState('');
  const { videoRef, erro, pronto } = useLeitorCodigo({ ativo: isOpen, onLeitura });

  const enviarManual = (evento: React.FormEvent) => {
    evento.preventDefault();
    const valor = codigoManual.trim();
    if (!valor) return;

    // Mesmo caminho da câmera: quem digita e quem escaneia caem na mesma
    // validação e recebem o mesmo retorno.
    onLeitura(valor);
    setCodigoManual('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ler etiqueta">
      <div className="space-y-4">
        {erro ? (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{MENSAGEM_ERRO_CAMERA[erro]}</span>
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-lg bg-black">
            <video
              ref={videoRef}
              playsInline
              muted
              className="h-64 w-full object-cover"
              aria-label="Câmera do leitor de etiquetas"
            />

            {!pronto && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80">
                <Camera className="h-8 w-8 animate-pulse" />
                <span className="text-sm">Abrindo a câmera...</span>
              </div>
            )}

            {pronto && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-40 w-40 rounded-lg border-2 border-white/70" />
              </div>
            )}

            {aviso && (
              <div
                className={`absolute inset-x-0 bottom-0 p-3 text-center text-sm font-medium text-white ${
                  aviso.tipo === 'sucesso' ? 'bg-green-600/90' : 'bg-red-600/90'
                }`}
              >
                {aviso.texto}
              </div>
            )}
          </div>
        )}

        {!erro && (
          <p className="text-center text-xs text-gray-500">
            Aponte para o QR da etiqueta. A câmera continua aberta para a próxima peça.
          </p>
        )}

        <form onSubmit={enviarManual} className="space-y-2 border-t pt-4">
          <label
            htmlFor="codigo-manual"
            className="flex items-center gap-2 text-sm font-medium text-gray-700"
          >
            <Keyboard className="h-4 w-4" />
            Digitar o código da etiqueta
          </label>
          <div className="flex gap-2">
            <input
              id="codigo-manual"
              type="text"
              inputMode="text"
              autoComplete="off"
              value={codigoManual}
              onChange={(e) => setCodigoManual(e.target.value)}
              placeholder="Ex.: 00042"
              className="focus:border-primary-500 focus:ring-primary-500 flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1"
            />
            <Button type="submit" variant="outline" disabled={!codigoManual.trim()}>
              Buscar
            </Button>
          </div>
        </form>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
